/**
 * Client-authored shapes for the meeting-booking slice: the two room
 * endpoints the in-chat booking dialog calls (`/api/calendar/rooms`,
 * `/api/calendar/favorite-rooms`) plus the invitation-attachment limits.
 *
 * The limits live here because three layers enforce the same rule — the
 * attach control caps how many files the user can pick, the approval-card
 * sanitizer bounds what a client may send back, and the `createEvent` tool
 * rejects anything over the per-file ceiling. When those numbers drift the
 * failure is silent: the user attaches a file the invitation never carries.
 *
 * Dates are ISO 8601 strings (plain JSON, no superjson).
 */

import { z } from "zod";

/**
 * Files one invitation may carry. Graph accepts more, but every attachment
 * is copied to every attendee's mailbox — past a handful, a link is the
 * right answer.
 */
export const EVENT_ATTACHMENT_MAX_COUNT = 5;

/** Per-file ceiling in whole MB — the number user-facing copy quotes. */
export const EVENT_ATTACHMENT_MAX_MB = 3;

/**
 * Graph's `fileAttachment` ceiling. Larger files need an upload session,
 * which the booking flow deliberately doesn't do (the staged create would
 * have to hold invitations while a multi-request upload runs).
 */
export const EVENT_ATTACHMENT_MAX_BYTES = EVENT_ATTACHMENT_MAX_MB * 1024 * 1024;

/**
 * Hard ceiling on stored favorite rooms — a favorites list longer than this
 * stops being "favorites" and the ranking boost would drown the
 * availability sort.
 */
export const MAX_FAVORITE_ROOMS = 20;

/** A free room the booking dialog can offer as a pill. */
export interface SdkBookableRoom {
  building?: string;
  capacity?: number;
  /** Resource-mailbox address — what `createEvent` books. */
  email: string;
  floorLabel?: string;
  /** The user starred this room — pills render it with a filled star. */
  isFavorite?: boolean;
  name: string;
}

/**
 * `GET /api/calendar/rooms` response. `timeZone` echoes the zone the
 * availability was evaluated in, so a client can label the result without
 * re-deriving it.
 */
export interface SdkRoomsResponse {
  rooms: SdkBookableRoom[];
  timeZone: string;
}

/** `GET /api/calendar/rooms` query, before URL encoding. */
export interface SdkRoomLookupQuery {
  /** Ranking signal: rooms the user has met these people in rank first. */
  attendees?: string[];
  /** Wall-clock `YYYY-MM-DDTHH:mm[:ss]` in {@link SdkRoomLookupQuery.timeZone}. */
  end: string;
  start: string;
  /** IANA zone the wall-clock bounds are expressed in. */
  timeZone: string;
}

/**
 * One starred conference room. The resource-mailbox address is the stable
 * identity; the name is denormalized at star time so pickers can label the
 * room without a directory round-trip.
 */
export interface SdkFavoriteRoom {
  /** Resource-mailbox address of the room, lowercased by the server. */
  email: string;
  name?: string;
}

/** `GET` / `PUT /api/calendar/favorite-rooms` response. */
export interface SdkFavoriteRoomsResponse {
  favorites: SdkFavoriteRoom[];
}

/**
 * `PUT /api/calendar/favorite-rooms` body — the FULL list on every
 * star/unstar toggle (last write wins). The route parses with this exact
 * schema, so the request shape needs no separate drift check.
 */
export const sdkFavoriteRoomsInputSchema = z.object({
  favorites: z
    .array(
      z.object({
        email: z.string().email(),
        name: z.string().max(200).optional(),
      })
    )
    .max(MAX_FAVORITE_ROOMS),
});

export type SdkFavoriteRoomsInput = z.infer<typeof sdkFavoriteRoomsInputSchema>;
